import type { TAnalyticsEvent, TAnalyticsTransport } from '@muse/analytics'
import { API_BASE_URL, SHARE_ANALYTICS_URL } from '../api/api'
import { deepKeysToSnakeCase } from './helpers'

const ANALYTICS_CONFIG = {
  endpoint: `${API_BASE_URL}/event/log`,
  batchSize: 2,
  flushInterval: 60000,
  maxRetries: 3,
  retryDelay: 1000,
}

const SHARE_ANALYTICS_CONFIG = {
  endpoint: SHARE_ANALYTICS_URL,
  batchSize: 2,
  flushInterval: 60000,
  maxRetries: 3,
  retryDelay: 1000,
}

let eventQueue: Array<{ event: TAnalyticsEvent; channel: string; timestamp: number }> = []
let flushTimer: NodeJS.Timeout | null = null

const sendBatch = async (events: typeof eventQueue) => {
  if (events.length === 0) return

  const payload = {
    events: events.map(({ event, channel, timestamp }) =>
      deepKeysToSnakeCase({
        ...(event as Record<string, unknown>),
        channel,
        timestamp,
      }),
    ),
  }

  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    const sent = navigator.sendBeacon(ANALYTICS_CONFIG.endpoint, blob)
    if (sent) {
      return
    }
  }

  try {
    const response = await fetch(ANALYTICS_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
  } catch (error) {
    console.error('[Analytics] Failed to send events:', error)
  }
}

const flushQueue = async () => {
  if (eventQueue.length === 0) return

  const eventsToSend = [...eventQueue]
  eventQueue = []

  await sendBatch(eventsToSend)
}

const scheduleFlush = () => {
  if (flushTimer) {
    clearTimeout(flushTimer)
  }

  flushTimer = setTimeout(() => {
    flushQueue()
    flushTimer = null
  }, ANALYTICS_CONFIG.flushInterval)
}

export const createHttpTransport = (): TAnalyticsTransport => {
  return {
    queue: (event: TAnalyticsEvent, channel: string) => {
      eventQueue.push({
        event,
        channel,
        timestamp: Date.now(),
      })

      if (eventQueue.length >= ANALYTICS_CONFIG.batchSize) {
        flushQueue()
      } else {
        scheduleFlush()
      }
    },

    send: async (event: TAnalyticsEvent, channel: string) => {
      const immediateBatch = [
        {
          event,
          channel,
          timestamp: Date.now(),
        },
      ]

      await sendBatch(immediateBatch)
    },
  }
}

export const createShareHttpTransport = (): TAnalyticsTransport => {
  const sendShareBatch = async (
    events: Array<{ event: TAnalyticsEvent; channel: string; timestamp: number }>,
  ) => {
    if (events.length === 0) return

    const payload = {
      events: events.map(({ event, channel, timestamp }) =>
        deepKeysToSnakeCase({
          ...(event as Record<string, unknown>),
          channel,
          timestamp,
        }),
      ),
    }

    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
      const sent = navigator.sendBeacon(SHARE_ANALYTICS_CONFIG.endpoint, blob)
      if (sent) return
    }

    try {
      await fetch(SHARE_ANALYTICS_CONFIG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.error('[Analytics] Failed to send share events:', error)
    }
  }

  return {
    queue: (event: TAnalyticsEvent, channel: string) => {
      sendShareBatch([{ event, channel, timestamp: Date.now() }])
    },
    send: async (event: TAnalyticsEvent, channel: string) => {
      await sendShareBatch([{ event, channel, timestamp: Date.now() }])
    },
  }
}

export const createLoggingTransport = (): TAnalyticsTransport => {
  return {
    queue: (_event: TAnalyticsEvent, _channel: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.group(`[Analytics] Event queued - Channel: ${_channel}`)
        console.log('Event:', _event)
        console.log('Channel:', _channel)
        console.log('Timestamp:', new Date().toISOString())
        console.groupEnd()
      }
    },

    send: (event: TAnalyticsEvent, channel: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.group(`[Analytics] Event sent immediately - Channel: ${channel}`)
        console.log('Event:', event)
        console.log('Channel:', channel)
        console.log('Timestamp:', new Date().toISOString())
        console.log('Method: sendImmediately')
        console.groupEnd()
      }
    },
  }
}
